import { z } from 'zod'
import { BaseFormAnswerSchema } from '@omnea-app/packages/universal/formBuilder/BaseFormQuestionAndAnswerSchemas'
import {
  BaseQuestionConfigSchema,
  BaseQuestionDefinitionSchema,
  BaseQuestionInstanceSchema,
} from '@omnea-app/packages/universal/formBuilder/question'

// Used in commit supplier data forms to configure how a supplier's product appears in the
// product catalogue.

export const ProductCatalogueAccessTypeSchema = z.enum(['EMAIL', 'DIRECT', 'TICKET', 'WORKFLOW'])
export type ProductCatalogueAccessType = z.infer<typeof ProductCatalogueAccessTypeSchema>

const AccessApproverSchema = z.object({
  id: z.uuid(),
  type: z.enum(['USER', 'GROUP']),
  name: z.string(),
})

export const ProductCatalogueAccessConfigBaseSchema = z.object({
  accessWorkflowId: z.string().nullable(),
  accessInstructions: z.string().nullable(),
  externalEmail: z.string().nullable(),
})

export const ProductCatalogueAccessValueSchema = z.object({
  ...ProductCatalogueAccessConfigBaseSchema.shape,
  showInCatalogue: z.boolean(),
  categoryIds: z.array(z.uuid()).optional(),
  accessApprover: AccessApproverSchema.nullable(),
  accessUrl: z.string().nullable(),
  accessType: ProductCatalogueAccessTypeSchema.nullable(),
})
export type ProductCatalogueAccessValue = z.infer<typeof ProductCatalogueAccessValueSchema>

export const ProductCatalogueAccessQuestionDefinitionSchema = z.object({
  ...BaseQuestionDefinitionSchema.shape,
  type: z.literal('productCatalogueAccess'),
})
export type ProductCatalogueAccessQuestionDefinition = z.infer<typeof ProductCatalogueAccessQuestionDefinitionSchema>

export const ProductCatalogueAccessQuestionInstanceSchema = z.object({
  ...BaseQuestionInstanceSchema.shape,
  type: z.literal('productCatalogueAccess'),
})
export type ProductCatalogueAccessQuestionInstance = z.infer<typeof ProductCatalogueAccessQuestionInstanceSchema>

export const ProductCatalogueAccessQuestionConfigSchema = ProductCatalogueAccessQuestionDefinitionSchema.merge(
  BaseQuestionConfigSchema
).extend({})
export type ProductCatalogueAccessQuestionConfig = z.infer<typeof ProductCatalogueAccessQuestionConfigSchema>

export const ProductCatalogueAccessFormQuestionSchema = ProductCatalogueAccessQuestionInstanceSchema.merge(
  ProductCatalogueAccessQuestionConfigSchema
)
export type ProductCatalogueAccessFormQuestion = z.infer<typeof ProductCatalogueAccessFormQuestionSchema>

export const ProductCatalogueAccessFormAnswerSchema = z.object({
  ...BaseFormAnswerSchema.shape,
  type: z.literal('productCatalogueAccess'),
  value: ProductCatalogueAccessValueSchema.nullable(),
})
export type ProductCatalogueAccessFormAnswer = z.infer<typeof ProductCatalogueAccessFormAnswerSchema>

export type ProductCatalogueAccessFieldError = 'required' | 'invalid'

export type ProductCatalogueAccessFormError = {
  showInCatalogue?: ProductCatalogueAccessFieldError
  accessType?: ProductCatalogueAccessFieldError
  accessUrl?: ProductCatalogueAccessFieldError
  accessWorkflowId?: ProductCatalogueAccessFieldError
  accessApprover?: ProductCatalogueAccessFieldError
  externalEmail?: ProductCatalogueAccessFieldError
  categoryIds?: ProductCatalogueAccessFieldError
}

export type ProductCatalogueAccessFormTouched = {
  showInCatalogue: boolean
  accessType: boolean
  accessUrl: boolean
  accessWorkflowId: boolean
  accessApprover: boolean
  externalEmail: boolean
  categoryIds: boolean
}
